import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Heart, Users, MapPin, Crown, Plus, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/013ytm7m_FB_IMG_1770979375236.jpg",
  drums: "https://customer-assets.emergentagent.com/job_sound-bath-hub/artifacts/xcrl3xni_FB_IMG_1770979546228.jpg",
  drum: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/vmj32u17_IMG-20260826-WA0053.webp",
  singingBowls: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/0yge4pwh_IMG-20260826-WA0054.webp",
};

const studioExperiences = [
  {
    id: "reset",
    name: "The Reset",
    subtitle: "1:1 Private Experience",
    emoji: "✨",
    description: "A deeply personal session for those seeking stillness, clarity, and energetic realignment.",
    details: [
      "60 minutes",
      "Tailored sound journey",
      "Gentle grounding and integration",
    ],
    pricing: [{ label: "Private Session", price: "£70" }],
  },
  {
    id: "connection",
    name: "The Connection",
    subtitle: "2 to 3 People",
    emoji: "✨",
    description: "A shared experience to relax, reconnect, and drop into harmony together.",
    details: [
      "60 minutes",
      "Intimate group setting",
      "Deep relaxation through sound",
    ],
    pricing: [
      { label: "2 people", price: "£55 per person" },
      { label: "3 people", price: "£50 per person" },
    ],
  },
  {
    id: "circle",
    name: "The Circle",
    subtitle: "4 to 6 People",
    emoji: "✨",
    description: "A nourishing group experience, perfect for gatherings, celebrations, or simply slowing down together.",
    details: [
      "60 minutes",
      "Full sound immersion",
      "Space to release, reset, and restore",
      "Maximum of 6 people lying down",
      "Minimum booking fee £120",
    ],
    pricing: [
      { label: "4 people", price: "£45 per person" },
      { label: "5 people", price: "£40 per person" },
      { label: "6 people", price: "£35 per person" },
    ],
  },
];

const mobileExperiences = {
  name: "The Experience",
  emoji: "✨",
  description: "A fully immersive sound journey brought to your chosen space. Transform your home, event, or workspace into a sanctuary.",
  perfect: "Perfect for birthdays, retreats, corporate wellbeing, and larger group experiences.",
  pricing: [
    { label: "Up to 6 people", price: "£200" },
    { label: "7 to 12 people", price: "£300" },
    { label: "13 to 20 people", price: "£400" },
    { label: "20+ people", price: "from £500 (custom quote)" },
  ],
  notes: [
    "Travel included within local areas",
    "Additional travel fees may apply depending on distance",
  ],
};

const sacredRitual = {
  name: "The Sacred Ritual",
  emoji: "👑",
  description: "A high touch, deeply intentional experience that blends sound, ceremony, and soul connection. This is your most premium offering, designed for those wanting something truly special and transformative.",
  includes: [
    "90 minute extended session",
    "Opening grounding and intention setting",
    "Ceremonial cacao ritual",
    "Immersive sound journey with crystal bowls, chimes and drums",
    "Energy balancing and intuitive guidance",
    "Oracle card reading to close",
  ],
  pricing: [
    { label: "1 to 2 people", price: "£180" },
    { label: "3 to 6 people", price: "£75 per person" },
  ],
  perfect: "Perfect for birthdays, life transitions, emotional healing, or meaningful shared experiences.",
};

const enhancements = [
  { name: "Extended session (90 minutes)", price: "£20 per person" },
  { name: "Cacao ritual", price: "£15 per person" },
  { name: "Oracle card guidance", price: "£10 per person" },
];

export default function SoundExperiencesPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    experience: "",
    date: "",
    groupSize: "",
    location: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.experience) {
      toast.error("Please fill in all required fields");
      return;
    }
    setIsSubmitting(true);
    
    // For now, show success message - in production this would send an email
    setTimeout(() => {
      toast.success("Thank you! Your enquiry has been sent. Faye will be in touch soon 💛");
      setFormData({
        name: "",
        email: "",
        experience: "",
        date: "",
        groupSize: "",
        location: "",
        message: "",
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div data-testid="sound-experiences-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="experiences-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Sound healing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pb-24 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Sacred Sound Experiences
            </p>
            <h1 className="heading-xl mb-6 text-foreground">
              Immersive Sound<br />
              <em className="italic">Journeys</em>
            </h1>
            <p className="body-text text-muted-foreground max-w-lg">
              Step into a deeply immersive sound journey designed to calm the nervous 
              system, awaken the body, and guide you back into alignment. Each experience 
              is intuitively led, blending movement, stillness, and healing frequencies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-spacing bg-muted" data-testid="intro-section">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="body-text text-muted-foreground text-lg leading-relaxed">
              Whether you come alone or share the space with others, every session is 
              held with intention, care, and presence inside <strong>Sacred Sounds Studio</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Studio Experiences */}
      <section className="section-spacing" data-testid="studio-experiences">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Sacred Sounds Studio
            </p>
            <h2 className="heading-lg text-foreground mb-4">
              Studio <em className="italic">Experiences</em>
            </h2>
            <p className="body-text text-muted-foreground max-w-xl mx-auto">
              Held inside Sacred Sounds Studio, an intimate and calming space 
              designed for deep rest and full body relaxation.
            </p>
          </motion.div>

          <div className="space-y-8">
            {studioExperiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-muted p-8 md:p-12 border border-border"
                data-testid={`experience-${exp.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-2xl">{exp.emoji}</span>
                      <div>
                        <h3 className="font-serif text-2xl text-foreground">{exp.name}</h3>
                        <p className="text-sm text-primary">{exp.subtitle}</p>
                      </div>
                    </div>
                    <p className="body-text text-muted-foreground mb-6">
                      {exp.description}
                    </p>
                    <ul className="space-y-2">
                      {exp.details.map((detail, i) => (
                        <li key={`detail-${detail.slice(0,20)}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-background p-6 border border-border">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary mb-4">
                      💛 Pricing
                    </p>
                    <div className="space-y-3">
                      {exp.pricing.map((price, i) => (
                        <div key={`price-${price.label}`} className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">{price.label}</span>
                          <span className="font-serif text-lg text-foreground">{price.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio Instruments */}
      <section className="py-16 bg-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.drum}
                alt="Meinl drum and Tibetan singing bowl in the studio"
                className="w-full h-full object-cover"
                data-testid="sound-drum-image"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.singingBowls}
                alt="Crystal singing bowls and floral arrangement"
                className="w-full h-full object-cover"
                data-testid="sound-bowls-image"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mobile Experiences */}
      <section className="section-spacing bg-muted" data-testid="mobile-experiences">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary" />
              <p className="text-xs tracking-[0.3em] uppercase text-primary">
                Mobile Experiences
              </p>
            </div>
            <h2 className="heading-lg text-foreground mb-4">
              Sound Journeys <em className="italic">To You</em>
            </h2>
            <p className="body-text text-muted-foreground max-w-xl mx-auto">
              Transform your home, event, or workspace into a sanctuary.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-background p-8 md:p-12 border border-border max-w-3xl mx-auto"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">{mobileExperiences.emoji}</span>
              <h3 className="font-serif text-2xl text-foreground">{mobileExperiences.name}</h3>
            </div>
            <p className="body-text text-muted-foreground mb-4">
              {mobileExperiences.description}
            </p>
            <p className="text-sm text-primary italic mb-8">
              {mobileExperiences.perfect}
            </p>

            <div className="bg-muted p-6 mb-6">
              <p className="text-xs tracking-[0.2em] uppercase text-primary mb-4">
                💛 Pricing
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {mobileExperiences.pricing.map((price, i) => (
                  <div key={`mprice-${price.label}`} className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{price.label}</span>
                    <span className="font-serif text-lg text-foreground">{price.price}</span>
                  </div>
                ))}
              </div>
            </div>

            <ul className="space-y-2">
              {mobileExperiences.notes.map((note, i) => (
                <li key={`note-${note.slice(0,20)}`} className="text-xs text-muted-foreground italic">
                  • {note}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Sacred Ritual */}
      <section className="section-spacing" data-testid="sacred-ritual">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-primary" />
              <p className="text-xs tracking-[0.3em] uppercase text-primary">
                Signature Experience
              </p>
            </div>
            <h2 className="heading-lg text-foreground">
              The Sacred <em className="italic">Ritual</em>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">{sacredRitual.emoji}</span>
                <h3 className="font-serif text-3xl text-foreground">{sacredRitual.name}</h3>
              </div>
              <p className="body-text text-muted-foreground mb-8">
                {sacredRitual.description}
              </p>

              <p className="text-xs tracking-[0.2em] uppercase text-primary mb-4">
                Includes
              </p>
              <ul className="space-y-3 mb-8">
                {sacredRitual.includes.map((item, i) => (
                  <li key={`inc-${item.slice(0,20)}`} className="flex items-start gap-3">
                    <Sparkles className="w-4 h-4 text-secondary mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-sm text-primary italic">
                {sacredRitual.perfect}
              </p>
            </div>

            <div className="bg-muted p-8 border border-border">
              <img
                src={IMAGES.drums}
                alt="Sacred Ritual"
                className="w-full aspect-video object-cover mb-8"
              />
              <p className="text-xs tracking-[0.2em] uppercase text-primary mb-4">
                💛 Pricing
              </p>
              <div className="space-y-4">
                {sacredRitual.pricing.map((price, i) => (
                  <div key={i} className="flex justify-between items-center py-3 border-b border-border last:border-0">
                    <span className="text-muted-foreground">{price.label}</span>
                    <span className="font-serif text-2xl text-foreground">{price.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhancements */}
      <section className="section-spacing bg-muted" data-testid="enhancements">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Plus className="w-5 h-5 text-primary" />
              <p className="text-xs tracking-[0.3em] uppercase text-primary">
                Enhancements
              </p>
            </div>
            <h2 className="heading-md text-foreground">
              Elevate Your <em className="italic">Session</em>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-xl mx-auto"
          >
            <div className="bg-background p-6 border border-border">
              {enhancements.map((item, i) => (
                <div 
                  key={i} 
                  className="flex justify-between items-center py-4 border-b border-border last:border-0"
                >
                  <span className="text-muted-foreground">{item.name}</span>
                  <span className="font-serif text-lg text-foreground">{item.price}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Booking Enquiry Form */}
      <section className="section-spacing" data-testid="booking-form">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-primary" />
              <p className="text-xs tracking-[0.3em] uppercase text-primary">
                Booking
              </p>
            </div>
            <h2 className="heading-lg text-foreground mb-4">
              Enquire <em className="italic">Now</em>
            </h2>
            <p className="body-text text-muted-foreground max-w-xl mx-auto">
              All sessions are booked via enquiry to allow for a personalised experience. 
              Please include your preferred date, group size, and whether you would like 
              a studio or mobile session.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sm mb-2 block">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="rounded-none border-border"
                    required
                    data-testid="enquiry-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="rounded-none border-border"
                    required
                    data-testid="enquiry-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="experience" className="text-sm mb-2 block">Experience *</Label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 rounded-none border border-border bg-background text-foreground"
                  required
                  data-testid="enquiry-experience"
                >
                  <option value="">Select an experience</option>
                  <option value="The Reset (1:1)">The Reset (1:1 Private)</option>
                  <option value="The Connection (2-3 people)">The Connection (2-3 people)</option>
                  <option value="The Circle (4-6 people)">The Circle (4-6 people)</option>
                  <option value="Mobile Experience">Mobile Experience</option>
                  <option value="The Sacred Ritual">The Sacred Ritual</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="date" className="text-sm mb-2 block">Preferred Date</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="rounded-none border-border"
                    data-testid="enquiry-date"
                  />
                </div>
                <div>
                  <Label htmlFor="groupSize" className="text-sm mb-2 block">Group Size</Label>
                  <Input
                    id="groupSize"
                    name="groupSize"
                    value={formData.groupSize}
                    onChange={handleInputChange}
                    placeholder="Number of people"
                    className="rounded-none border-border"
                    data-testid="enquiry-group-size"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location" className="text-sm mb-2 block">Studio or Mobile?</Label>
                <select
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 rounded-none border border-border bg-background text-foreground"
                  data-testid="enquiry-location"
                >
                  <option value="">Select location preference</option>
                  <option value="Studio (Sacred Sounds Studio)">Studio (Sacred Sounds Studio)</option>
                  <option value="Mobile (You provide location)">Mobile (You provide location)</option>
                </select>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm mb-2 block">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell me a bit about what you're looking for..."
                  className="rounded-none border-border min-h-[120px]"
                  data-testid="enquiry-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-xs tracking-[0.2em] uppercase"
                data-testid="enquiry-submit"
              >
                {isSubmitting ? "Sending..." : "Send Enquiry"}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary" data-testid="experiences-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <p className="body-text text-primary-foreground/80 mb-4">
              If you feel called, trust that. 🤍
            </p>
            <h2 className="heading-lg mb-10 text-primary-foreground">
              Your Journey <em className="italic">Awaits</em>
            </h2>
            <Link to="/gift-vouchers">
              <Button
                size="lg"
                className="rounded-none bg-background hover:bg-background/90 text-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                data-testid="gift-experience-btn"
              >
                Gift an Experience
                <ArrowRight className="ml-3 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
